import { displayToast } from "../../../components/app/AppToast";
import { SoundEffect } from "../../../interfaces";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { gaEventContentSave } from "../../../tools/analytics/domains/content.analytics";
import {
  convertVideoToItemData,
  gaEventVideoSave,
} from "../../../tools/analytics/domains/video.analytics";
import { iosPlaySound } from "../../../tools/ios";
import { useGetUserCreatedPlaylistsQuery } from "./useGetUserCreatedPlaylistsQuery";
import { apiClient, useApiRevalidate, type Api } from "../../../services/api";
import { useOnboardingGuard } from "./useOnboardingGuard";
import { useTranslation } from "react-i18next";

type VideoListItem = Extract<
  Api["get"]["/webapp/public/content"]["output"]["body"]["docs"][number],
  { content_format: "Video" }
>;

export function useSaveVideo(
  video: VideoListItem | null,
  analyticsListName: AnalyticsItemList
) {
  const { t } = useTranslation();
  const { playlists: createdPlaylists } = useGetUserCreatedPlaylistsQuery();
  const revalidate = useApiRevalidate();
  const onboardingGuard = useOnboardingGuard();
  const isSaved = createdPlaylists.some((playlist) => {
    return (playlist.playlistContent ?? []).some((id) => {
      return id === video?.id;
    });
  });

  async function toggleSaveToPlaylists(
    video: VideoListItem,
    { add, remove }: { add: string[]; remove: string[] }
  ) {
    try {
      if (
        !onboardingGuard(
          t("🔒 Please complete your profile first to save this content. ")
        )
      ) {
        return;
      }

      await apiClient.post("/webapp/private/playlists/batch/content", {
        json: { contentId: video.id, add, remove },
      });

      if (add.length > 0) {
        iosPlaySound(SoundEffect.SAVE);
        gaEventVideoSave({
          video,
          listName: analyticsListName,
        });
        gaEventContentSave(
          convertVideoToItemData({ video, listName: analyticsListName })
        );
      }

      displayToast(
        add.length > 0
          ? t("Video saved to your collection")
          : t("Video removed from your collection"),
        "success"
      );

      // Non-critical: refresh caches (fire-and-forget)
      void revalidate("/webapp/private/playlists/me");
      // The desktop bookmark reads the Saved list, which counts collections.
      void revalidate("/webapp/private/saved");
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return {
    isSaved,
    toggleSaveToPlaylists,
  };
}
